import React from 'react';

import { cn, DialogFooter, Progress, Typography } from '@crossnokaye/ux-common';

import { TooltipButton } from '@/components/molecules/tooltip-button';
import { Button } from '@/ux-common/components/button/button';

import { SaveProgressSummary } from './save-progress-summary';
import type { PreviewSaveStatus } from './setting-preview.types';

interface SettingsSaveFooterProps {
  hasPendingSave: boolean;
  isCancelled: boolean;
  isSubmitting: boolean;
  saveComplete: boolean;
  allSucceeded: boolean;
  changeCount: number;
  saveProgressByChangeId: Record<string, PreviewSaveStatus>;
  progressPercent: number;
  disableSave: boolean;
  permissionTooltipText?: string;
  saveDisabledByPermissions?: boolean;
  onCancelPending: () => void;
  onClose: () => void;
  onConfirm: () => void;
}

const getDisabledSaveTooltip = (
  disableSave: boolean,
  changeCount: number,
  isSubmitting: boolean
): string | undefined => {
  if (disableSave) return 'One or more changes have validation errors';
  if (changeCount === 0) return 'No pending changes to save';
  if (isSubmitting) return 'Save is already in progress';
  return undefined;
};

export const SettingsSaveFooter = ({
  hasPendingSave,
  isCancelled,
  isSubmitting,
  saveComplete,
  allSucceeded,
  changeCount,
  saveProgressByChangeId,
  progressPercent,
  disableSave,
  permissionTooltipText,
  saveDisabledByPermissions = false,
  onCancelPending,
  onClose,
  onConfirm,
}: SettingsSaveFooterProps): React.ReactElement => {
  const hasSaveProgress = Object.keys(saveProgressByChangeId).length > 0;
  const confirmDisabled =
    isSubmitting || changeCount === 0 || disableSave || saveDisabledByPermissions;
  const confirmTooltipContent = saveDisabledByPermissions
    ? permissionTooltipText
    : getDisabledSaveTooltip(disableSave, changeCount, isSubmitting);

  const progressSection = hasSaveProgress ? (
    <div className="space-y-4 w-full">
      {hasSaveProgress && <Progress value={progressPercent} className="h-1.5" />}
      <SaveProgressSummary progressMap={saveProgressByChangeId} />
    </div>
  ) : null;

  if (hasPendingSave) {
    return (
      <div className="shrink-0 flex flex-col gap-3 p-stack-sm sm:p-none">
        {progressSection}
        <DialogFooter className="flex gap-2 flex-row justify-center sm:justify-end p-0">
          <Typography
            variant="body"
            className="text-xs text-ckds-text-fg-tier3 mr-auto self-center"
          >
            Cancelling will only stop queued items. Items already submitted will finish.
          </Typography>
          <Button
            type="button"
            disabled={isCancelled}
            onClick={onCancelPending}
            className="btn btn-tier3 flex-1 sm:flex-none"
          >
            Cancel queued
          </Button>
          <Button
            type="button"
            disabled={isCancelled || isSubmitting}
            className="btn btn-brand flex-1 sm:flex-none"
          >
            Saving...
          </Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <div className="shrink-0 flex flex-col gap-3 p-stack-sm sm:p-none">
      {progressSection}
      <DialogFooter className="flex gap-2 flex-row justify-center sm:justify-end p-0">
        <Button
          type="button"
          className={cn(
            'btn',
            allSucceeded || saveComplete ? 'btn-brand' : 'btn-tier3',
            'sm:w-36 flex-1 sm:flex-none'
          )}
          onClick={onClose}
        >
          Close
        </Button>
        {!saveComplete && (
          <TooltipButton
            type="button"
            disabled={confirmDisabled}
            onClick={onConfirm}
            className="flex-1 sm:flex-none btn btn-brand"
            tooltipContent={confirmTooltipContent}
            delayDuration={0}
          >
            {`SAVE ${changeCount} CHANGE${changeCount !== 1 ? 'S' : ''}`}
          </TooltipButton>
        )}
      </DialogFooter>
    </div>
  );
};
